import clsx from 'clsx'
import { WideLayout } from 'layouts'
import formsMap from 'modules/Forms/Account/Create'
import formStyles from 'modules/Forms/common-form.module.scss'
import React, { useCallback, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, useHistory, useRouteMatch } from 'react-router'
import validator from 'utils/asyncValidator'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import PropTypes from 'prop-types'
import { CONFIRM_EXIT_FROM_CREATE_ACCOUNT } from 'modules/Services/types'
import { useNetworksData } from 'modules/Services/hooks/useNetworksData'
import { clearFormSchema, setFormSchema } from 'modules/Forms/FormMeta/actions'
import extractRequiredFields from 'utils/extractRequiredFields'

const AccountCreate = ({ multiLogins = false }) => {
  const pathGen = useGeneratePathI18n()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const {
    params: { networkType },
  } = useRouteMatch()

  const { networkId } = useNetworksData(networkType)

  const { bulk_account_creation_allowed } = useSelector((state) => state['services/network'])

  const formConfig = useMemo(() => formsMap?.[networkType], [networkType])

  if (!formConfig) {
    return <Redirect to={pathGen(ROUTES.SERVICES)} />
  }

  const { component: FormComponent, formName } = useMemo(() => formConfig, [formConfig])

  const schema = useMemo(() => formConfig.schema(networkType), [formConfig, networkType])

  useEffect(() => {
    if (formName && schema) {
      dispatch(setFormSchema(formName, extractRequiredFields(schema)))
    }
    return () => {
      if (formName) {
        dispatch(clearFormSchema(formName))
      }
    }
  }, [formName, schema])

  const navigateToNetwork = useCallback(() => {
    history.push(
      pathGen(ROUTES.SERVICES_NETWORK, {
        networkType,
      }),
    )
  }, [networkType])

  const getFormState = useCallback(() => {
    try {
      const config = localStorage?.getItem('CreateAccountConfig') ?? '{}'
      return JSON.parse(config)
    } catch {
      return {}
    }
  }, [])

  const handleClose = useCallback(
    ({ confirm = true }) => {
      const { anyTouched = false, submitSucceeded = false } = getFormState()

      if (!confirm || !anyTouched || submitSucceeded) {
        navigateToNetwork()
        return
      }

      dispatch({
        type: CONFIRM_EXIT_FROM_CREATE_ACCOUNT,
        payload: {
          title: t('services::forms.register_account.confirm_exit.title'),
          text: t('services::forms.register_account.confirm_exit.summary'),
          buttons: {
            cancel: t('services::forms.register_account.confirm_exit.buttons.cancel'),
            apply: t('services::forms.register_account.confirm_exit.buttons.apply'),
          },
          onConfirm: navigateToNetwork,
        },
      })
    },
    [networkType, getFormState, navigateToNetwork],
  )

  return (
    <>
      <Helmet defer={false}>
        <title>
          {t('common::nav_menu.network_account_register')} {t(`services::networks.${networkType}`)}
        </title>
      </Helmet>

      <WideLayout onClose={handleClose}>
        <div className={clsx(formStyles['formContainer__inner'])}>
          {networkType && (
            <FormComponent
              multiLogins={multiLogins && bulk_account_creation_allowed}
              touchOnChange
              networkType={networkType}
              networkId={networkId}
              onClose={handleClose}
              asyncValidate={validator(schema)}
            />
          )}
        </div>
      </WideLayout>
    </>
  )
}

export default AccountCreate

AccountCreate.propTypes = {
  multiLogins: PropTypes.bool,
}
